/**
 * Which commit this bundle was built from — audit finding **A3**, frontend half.
 *
 * The values are spliced in at build time by vite `define` (see `vite.config.ts`) and also
 * written to a static `build-info.json` an operator can `curl` without loading the app. The
 * procedure that reads them is `docs/deploy-runbook.md` § Verify.
 *
 * ⚠ **Why `define` and not a `VITE_*` variable.** An unset `VITE_*` var inlines as the **literal
 * string `"undefined"`** — non-empty, truthy, and indistinguishable from a real value at a
 * glance. That is the mechanism behind this app's 2026-07-17 AADSTS900023 outage. `define`
 * splices JSON, so an absent value arrives as `null` and reads as absent. The normaliser below
 * still rejects `"undefined"` by name, because defence that costs one array entry is worth
 * keeping on the path where a wrong answer ends an investigation.
 */

/** Values that are present but meaningless. Rejected by NAME as well as by shape. */
const NOT_A_REAL_SHA = ['unknown', 'undefined', 'null', 'head'];

const SHA_LENGTH = 40;

/**
 * Returns `raw` as a lowercase 40-character sha, or `null` if it is anything else.
 *
 * 🔑 `null` is the honest answer and callers must render it as "unknown" rather than substituting
 * something. **An absent version prompts a question; a wrong one ends the investigation.**
 */
export function normalizeBuildSha(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;

  const value = raw.trim();
  if (value.length === 0) return null;
  if (NOT_A_REAL_SHA.includes(value.toLowerCase())) return null;

  // A git sha is exactly 40 hex characters. Anything else ended up in the variable by accident —
  // an error message, a path, a shell echo — and must not be reported as a commit.
  if (value.length !== SHA_LENGTH) return null;
  if (!/^[0-9a-f]{40}$/i.test(value)) return null;

  // git's null object id: 40 valid hex characters that identify nothing.
  if (/^0{40}$/.test(value)) return null;

  return value.toLowerCase();
}

/** First 7 characters — what a human compares against `git rev-parse --short=7 HEAD`. */
export function shortSha(sha: string | null): string | null {
  return sha === null ? null : sha.slice(0, 7);
}

export interface BuildInfo {
  buildSha: string | null;
  buildShaShort: string | null;
  /**
   * True when the working tree had uncommitted changes at build time — the SPA ships by
   * `swa deploy ./dist`, which uploads whatever was built, so this can happen from a laptop.
   * When true, `buildSha` names the build's ANCESTOR rather than the build.
   */
  buildDirty: boolean;
}

/**
 * Reads the values vite spliced in. Guarded with `typeof` so importing this module outside a
 * vite build (a unit test, an SSR pass) yields "unknown" rather than a ReferenceError.
 */
export function readBuildInfo(): BuildInfo {
  const rawSha = typeof __BUILD_SHA__ === 'undefined' ? null : __BUILD_SHA__;
  const rawDirty = typeof __BUILD_DIRTY__ === 'undefined' ? false : __BUILD_DIRTY__;

  const buildSha = normalizeBuildSha(rawSha);
  return {
    buildSha,
    buildShaShort: shortSha(buildSha),
    buildDirty: rawDirty === true,
  };
}
